import {
  DeleteOutlined,
  DownOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { Select, Tooltip } from "antd";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import LinkIcon from "components/svg-icons/link-icon";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import "./feature-item.scss";

type TMilestone = {
  id: string;
  name: string;
};

function CompactFeatureItem(
  props: Readonly<{
    label: string;
    onUnlink: (feature: any) => void;
    disableUnlink: boolean;
    nestedInfoTooltip?: string;
  }>
) {
  const { label, onUnlink, disableUnlink, nestedInfoTooltip } = props;

  return (
    <div className="feature-item-compact" data-testid="featureitem-compact">
      <div className="feature-item-compact__row">
        <LinkIcon className="feature-item-compact__icon" />
        <div className="feature-item-compact__title">
          <TruncatedTextWithTooltip
            text={label}
            dataTestId="featureitem-text-combined"
          />
        </div>
        {nestedInfoTooltip ? (
          <Tooltip title={nestedInfoTooltip} placement="top">
            <InfoCircleOutlined
              className="feature-item-compact__info"
              data-testid="featureitem-nested-info"
            />
          </Tooltip>
        ) : null}
        <button
          type="button"
          className="feature-item-compact__unlink"
          disabled={disableUnlink}
          onClick={onUnlink}
          data-testid="featureitem-button-unlink"
        >
          Unlink
        </button>
      </div>
    </div>
  );
}

function isTextOverflowing(element: HTMLElement | null) {
  if (!element) {
    return false;
  }
  return element.scrollWidth > element.clientWidth + 1;
}

function FeatureItem(props: {
  number: string;
  title: string;
  onUnlink: (feature: any) => void;
  milestones?: TMilestone[];
  selectedFeatureMilestone?: TMilestone;
  showMilestoneSelect?: boolean;
  milestoneSelectLabel?: string;
  onMilestoneChange?: (milestone: TMilestone) => void;
  combineNumberAndTitle?: boolean;
  disableUnlink?: boolean;
  disableMilestoneSelect?: boolean;
  variant?: "default" | "compact";
  nestedInfoTooltip?: string;
}) {
  const {
    number,
    title,
    onUnlink,
    disableUnlink = false,
    disableMilestoneSelect = false,
    milestones = [],
    selectedFeatureMilestone,
    showMilestoneSelect = false,
    milestoneSelectLabel = "Linked from",
    onMilestoneChange,
    combineNumberAndTitle = false,
    variant = "default",
    nestedInfoTooltip
  } = props;
  const milestoneOptions = milestones.map((milestone) => ({
    label: milestone.name,
    value: milestone.id
  }));

  const milestoneSelectRef = useRef<HTMLDivElement>(null);
  const [milestoneSelectionTruncated, setMilestoneSelectionTruncated] =
    useState(false);

  const clearMilestoneSelectionTitle = () => {
    milestoneSelectRef.current
      ?.querySelector(".ant-select-content")
      ?.removeAttribute("title");
  };

  const measureMilestoneSelection = useCallback(() => {
    clearMilestoneSelectionTitle();
    const selectionItem = milestoneSelectRef.current?.querySelector(
      ".ant-select-content"
    ) as HTMLElement | null;
    const truncated = isTextOverflowing(selectionItem);
    setMilestoneSelectionTruncated((prev) =>
      prev === truncated ? prev : truncated
    );
  }, []);

  useEffect(() => {
    clearMilestoneSelectionTitle();
  }, [selectedFeatureMilestone?.id]);

  useLayoutEffect(() => {
    measureMilestoneSelection();

    const container = milestoneSelectRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(measureMilestoneSelection);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureMilestoneSelection, selectedFeatureMilestone?.id]);

  const combinedLabel = [number, title].filter((part) => part.trim()).join(" ");

  if (variant === "compact") {
    return (
      <CompactFeatureItem
        label={combineNumberAndTitle ? combinedLabel : title}
        onUnlink={onUnlink}
        disableUnlink={disableUnlink}
        nestedInfoTooltip={nestedInfoTooltip}
      />
    );
  }

  const milestoneSelect = (
    <Select
      size="small"
      className="w-full feature-item-milestone-select"
      classNames={{ popup: { root: "feature-item-milestone-select-dropdown" } }}
      value={selectedFeatureMilestone?.id}
      options={milestoneOptions}
      optionFilterProp="label"
      showSearch
      suffixIcon={<DownOutlined />}
      disabled={!milestoneOptions.length || disableMilestoneSelect}
      onChange={(milestoneId) => {
        const milestone = milestones.find((item) => item.id === milestoneId);
        // Options are built from `milestones`, so onChange's value is always a known milestone id; find() never returns undefined and this guard's false arm is unreachable via the UI.
        /* v8 ignore next 3 */
        if (milestone) {
          onMilestoneChange?.(milestone);
        }
      }}
      data-testid="featureitem-select-milestone"
    />
  );

  return (
    <div className="w-full overflow-hidden bg-white border border-solid border-ciq-gray-300 bg-transparent m-0 text-left space-y-1 rounded">
      <div className="w-full min-w-0 flex space-x-1 p-2">
        <div className="shrink-0 flex h-full">
          <LinkIcon className="text-ciq-primary-900 hover:text-ciq-primary-900" />
        </div>
        <div className="min-w-0 grow flex flex-col space-y-1.5">
          <div className="flex min-w-0 gap-x-1">
            <div className="min-w-0 flex-1 overflow-hidden text-ciq-gray-950">
              {combineNumberAndTitle ? (
                <TruncatedTextWithTooltip
                  text={combinedLabel}
                  dataTestId="featureitem-text-combined"
                  className="font-medium"
                />
              ) : (
                <div className="flex min-w-0 gap-x-1 overflow-hidden">
                  <div className="shrink-0 max-w-[40%] min-w-0">
                    <TruncatedTextWithTooltip
                      text={number}
                      dataTestId="featureitem-text-number"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <TruncatedTextWithTooltip
                      text={title}
                      dataTestId="featureitem-text-title"
                      className="font-medium"
                    />
                  </div>
                </div>
              )}
            </div>
            <DeleteOutlined
              className={`unlink-button${
                disableUnlink
                  ? " opacity-40 cursor-not-allowed pointer-events-none"
                  : ""
              }`}
              onClick={disableUnlink ? undefined : onUnlink}
              data-testid="featureitem-button-unlink"
            />
          </div>
          {showMilestoneSelect ? (
            <div className="flex min-w-0 items-center gap-x-1">
              <div className="shrink-0 text-ciq-black text-opacity-60">
                {milestoneSelectLabel}
              </div>
              <div
                ref={milestoneSelectRef}
                className="min-w-0 flex-1"
                onMouseEnter={measureMilestoneSelection}
              >
                <Tooltip
                  title={
                    milestoneSelectionTruncated &&
                    selectedFeatureMilestone?.name
                      ? selectedFeatureMilestone.name
                      : undefined
                  }
                  placement="topLeft"
                  getPopupContainer={() => document.body}
                  zIndex={1060}
                >
                  {milestoneSelect}
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default FeatureItem;
